import Link from 'next/link';
import React from 'react';
import { NoFollowLink } from 'shared/no-follow-link';
import { getAssetPath } from 'utils/asset-utils';
import { getBlogUrl, getFrontEndUrl } from 'utils/environment-util';

const SHEF_LOGO_PATH = getAssetPath('/images/CDN/shef-logo.svg');
const APP_STORE_LINK_PATH = getAssetPath('/images/app-store-link.svg');
const GOOGLE_PLAY_LINK_PATH = getAssetPath('/images/google-play-link.svg');
const PLAID_FOOTER_BG_PATH = getAssetPath('/images/CDN/plaid-cloth-footer.svg');
const SOCIAL_FACEBOOK_PATH = getAssetPath('/images/CDN/social-facebook.svg');
const SOCIAL_INSTAGRAM_PATH = getAssetPath('/images/CDN/social-instagram.svg');
const SOCIAL_TIKTOK_PATH = getAssetPath('/images/CDN/social-tiktok.svg');

const GOOGLE_PLAY_HREF = 'https://play.google.com/store/apps/details?id=com.shef.consumerapp&hl=en_US&gl=US&pli=1';
const APP_STORE_HREF = 'https://apps.apple.com/us/app/shef-homemade-food-delivery/id1598007796';

interface IProps {
  className?: string;
}

export const Footer: React.FC<IProps> = ({ className }) => {
  const frontEndUrl = getFrontEndUrl();
  const blogUrl = getBlogUrl();
  return (
    <div className={`bg-eggplant-50 ${className}`}>
      {/* Desktop Layout */}
      <div className='hidden md:block'>
        <div className='my-8 border-t border-eggplant-100 md:my-0'></div>
        <div className='flex h-full grid-cols-2 gap-4 bg-eggplant-50 px-14 pt-24'>
          <section className='w-full'>
            <img src={SHEF_LOGO_PATH} alt='Shef' className='mb-4 mt-1 inline-block w-20' />
            <div className='mx-auto flex-col items-center'>
              <h3 className='mx-auto w-80 text-1xl font-bold text-eggplant-600 md:mx-0'>
                DOWNLOAD OUR APP AND LEAVE THE REST TO THE SHEFS
              </h3>
              <div className='flex justify-center gap-6 pt-4 md:justify-start'>
                <NoFollowLink href={APP_STORE_HREF} className='text-eggplant-600 hover:text-radish-200'>
                  <img src={APP_STORE_LINK_PATH} alt='Download on the App Store' />
                </NoFollowLink>
                <NoFollowLink href={GOOGLE_PLAY_HREF} className='text-eggplant-600 hover:text-radish-200'>
                  <img src={GOOGLE_PLAY_LINK_PATH} alt='Get it on Google Play' />
                </NoFollowLink>
              </div>
            </div>
            <div className='mt-4'>Shef, Inc. {new Date().getFullYear()}. All Rights Reserved.</div>
          </section>

          <div className='flex w-full align-top'>
            <section className='flex justify-center gap-20 md:w-full md:grid-cols-3 md:gap-3 md:px-0'>
              <div className='md:w-[30%]'>
                <div className='flex min-w-[11rem] flex-col text-left'>
                  <span className='mb-8 text-xs font-semibold text-neutral-800'>LEARN</span>
                  <div className='flex flex-col gap-1 text-eggplant-400'>
                    <NoFollowLink href={`${blogUrl}/our-story`} className='text-eggplant-600 hover:text-radish-200'>
                      Our Story
                    </NoFollowLink>
                    <NoFollowLink href={`${blogUrl}/food-safety`} className='text-eggplant-600 hover:text-radish-200'>
                      Food Safety
                    </NoFollowLink>
                    <NoFollowLink href='https://help.shef.com/en/' className='text-eggplant-600 hover:text-radish-200'>
                      Customer Support
                    </NoFollowLink>
                  </div>
                </div>
              </div>

              <div className='md:w-[30%]'>
                <div className='flex min-w-[11rem] flex-col text-left'>
                  <span className='mb-8 text-xs font-semibold text-neutral-800'>RESOURCES</span>
                  <div className='flex flex-col gap-1 text-eggplant-400'>
                    <Link href={`${frontEndUrl}/become-a-shef`} className='text-eggplant-600 hover:text-radish-200'>
                      Become a shef
                    </Link>
                    <Link href={`${frontEndUrl}/careers`} className='text-eggplant-600 hover:text-radish-200'>
                      Careers
                    </Link>
                    <Link href={`${frontEndUrl}/gift`} className='text-eggplant-600 hover:text-radish-200'>
                      Gift cards
                    </Link>
                    <NoFollowLink
                      href={`${blogUrl}/partner-with-us`}
                      className='text-eggplant-600 hover:text-radish-200'>
                      Partnerships
                    </NoFollowLink>
                    <NoFollowLink
                      href={`${blogUrl}/business-orders`}
                      className='text-eggplant-600 hover:text-radish-200'>
                      For Business
                    </NoFollowLink>
                    <NoFollowLink
                      href={`${frontEndUrl}/homemade-food-delivery`}
                      className='whitespace-nowrap text-eggplant-600 hover:text-radish-200'>
                      Homemade food delivery
                    </NoFollowLink>
                    <div className='flex justify-center gap-4 md:flex-col md:gap-1'>
                      <Link
                        href={`${frontEndUrl}/terms-of-service`}
                        className='text-eggplant-600 hover:text-radish-200'>
                        Terms of Service
                      </Link>
                      <Link href={`${frontEndUrl}/privacy-policy`} className='text-eggplant-600 hover:text-radish-200'>
                        Privacy Policy
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <div className='md:w-[30%]'>
                <section className='flex justify-center gap-10 py-8 md:justify-start md:gap-2 md:p-0'>
                  <NoFollowLink
                    href='https://www.facebook.com/ordershef'
                    className='text-eggplant-600 hover:text-radish-200'>
                    <img src={SOCIAL_FACEBOOK_PATH} alt='Facebook' className='size-6' />
                  </NoFollowLink>
                  <NoFollowLink
                    href='https://www.instagram.com/ordershef'
                    className='text-eggplant-600 hover:text-radish-200'>
                    <img src={SOCIAL_INSTAGRAM_PATH} alt='Instagram' className='size-6' />
                  </NoFollowLink>
                  <NoFollowLink
                    href='https://www.tiktok.com/@ordershef'
                    className='text-eggplant-600 hover:text-radish-200'>
                    <img src={SOCIAL_TIKTOK_PATH} alt='TikTok' className='size-6' />
                  </NoFollowLink>
                </section>
              </div>
            </section>
          </div>
        </div>
        <img src={PLAID_FOOTER_BG_PATH} alt='' className='w-full pt-24' />
      </div>

      {/* Mobile Layout */}
      <div className='h-full flex-col items-center justify-center bg-eggplant-50 pt-16 text-center md:hidden'>
        <div className='mx-auto flex-col items-center'>
          <h3 className='mx-auto w-80 text-1xl font-bold text-eggplant-600 md:mx-0'>
            DOWNLOAD OUR APP AND LEAVE THE REST TO THE SHEFS
          </h3>
          <div className='flex justify-center gap-6 pt-4 md:justify-start'>
            <NoFollowLink href={APP_STORE_HREF} className='text-eggplant-600 hover:text-radish-200'>
              <img src={APP_STORE_LINK_PATH} alt='Download on the App Store' />
            </NoFollowLink>
            <NoFollowLink href={GOOGLE_PLAY_HREF} className='text-eggplant-600 hover:text-radish-200'>
              <img src={GOOGLE_PLAY_LINK_PATH} alt='Get it on Google Play' />
            </NoFollowLink>
          </div>
        </div>

        <div className='px-4'>
          <div className='my-8 border-t border-eggplant-100 md:my-0'></div>
          <section className='flex justify-center gap-20 md:w-full md:grid-cols-3 md:gap-3 md:px-0'>
            <div className='md:w-[30%]'>
              <div className='flex min-w-[11rem] flex-col text-left'>
                <span className='mb-8 text-xs font-semibold text-neutral-800'>LEARN</span>
                <div className='flex flex-col gap-1 text-eggplant-400'>
                  <NoFollowLink href={`${blogUrl}/our-story`} className='text-eggplant-600 hover:text-radish-200'>
                    Our Story
                  </NoFollowLink>
                  <NoFollowLink href={`${blogUrl}/food-safety`} className='text-eggplant-600 hover:text-radish-200'>
                    Food Safety
                  </NoFollowLink>
                  <NoFollowLink href='https://help.shef.com/en/' className='text-eggplant-600 hover:text-radish-200'>
                    Customer Support
                  </NoFollowLink>
                </div>
              </div>
            </div>

            <div className='md:w-[30%]'>
              <div className='flex w-28 flex-col text-left'>
                <span className='mb-8 text-xs font-semibold text-neutral-800'>RESOURCES</span>
                <div className='flex flex-col gap-1 text-eggplant-400'>
                  <Link href={`${frontEndUrl}/become-a-shef`} className='text-eggplant-600 hover:text-radish-200'>
                    Become a shef
                  </Link>
                  <Link href={`${frontEndUrl}/careers`} className='text-eggplant-600 hover:text-radish-200'>
                    Careers
                  </Link>
                  <Link href={`${frontEndUrl}/gift`} className='text-eggplant-600 hover:text-radish-200'>
                    Gift cards
                  </Link>
                  <NoFollowLink href={`${blogUrl}/partner-with-us`} className='text-eggplant-600 hover:text-radish-200'>
                    Partnerships
                  </NoFollowLink>
                  <NoFollowLink href={`${blogUrl}/business-orders`} className='text-eggplant-600 hover:text-radish-200'>
                    For Business
                  </NoFollowLink>
                  <NoFollowLink
                    href={`${frontEndUrl}/homemade-food-delivery`}
                    className='text-eggplant-600 hover:text-radish-200'>
                    Homemade food delivery
                  </NoFollowLink>
                </div>
              </div>
            </div>
          </section>

          <div className='my-8 border-t border-eggplant-100 md:my-0'></div>
          <img src={SHEF_LOGO_PATH} alt='Shef' className='mt-1 inline-block' />

          <section className='flex justify-center gap-10 py-8 md:justify-start md:gap-2 md:p-0'>
            <NoFollowLink href='https://www.facebook.com/ordershef' className='text-eggplant-600 hover:text-radish-200'>
              <img src={SOCIAL_FACEBOOK_PATH} alt='Facebook' className='size-6' />
            </NoFollowLink>
            <NoFollowLink
              href='https://www.instagram.com/ordershef'
              className='text-eggplant-600 hover:text-radish-200'>
              <img src={SOCIAL_INSTAGRAM_PATH} alt='Instagram' className='size-6' />
            </NoFollowLink>
            <NoFollowLink href='https://www.tiktok.com/@ordershef' className='text-eggplant-600 hover:text-radish-200'>
              <img src={SOCIAL_TIKTOK_PATH} alt='TikTok' className='size-6' />
            </NoFollowLink>
          </section>

          <div className='flex justify-center gap-4 md:flex-col md:gap-1'>
            <Link href={`${frontEndUrl}/terms-of-service`} className='text-eggplant-600 hover:text-radish-200'>
              Terms of Service
            </Link>
            <Link href={`${frontEndUrl}/privacy-policy`} className='text-eggplant-600 hover:text-radish-200'>
              Privacy Policy
            </Link>
          </div>

          <div className='mt-4'>Shef, Inc. {new Date().getFullYear()}. All Rights Reserved.</div>
        </div>
        <img src={PLAID_FOOTER_BG_PATH} alt='' className='w-full pt-24' />
      </div>
    </div>
  );
};
